import axios, { AxiosInstance } from 'axios';
import keycloakAuth from './KeycloakAuth';
import { Voucher, VoucherCreateRequest, VoucherPaymentRequest, StatusHistoryResponse } from '../types/voucher';
import {
  Account,
  AccountTransaction,
  AccountPosting,
  FloatLoad,
  AccountStatement,
  FloatLoadRequest,
  AccountSearchParams,
  TransactionSearchParams,
  PostingSearchParams,
  AccountStatementParams,
  PaginatedResponse,
} from '../types/account';
import {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  UpdatePasswordRequest,
  ResetPasswordRequest,
  UserProfile,
  UserSearchParams,
  PaginatedUsersResponse,
  UserStatistics,
} from '../types/user';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/vouchers';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = keycloakAuth.getToken();
    if (token) {
      // Ensure headers object exists
      if (!config.headers) {
        config.headers = {} as any;
      }
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('🔑 API request with auth token to:', config.url);
      console.log('🔑 Token:', token.substring(0, 50) + '...');
    } else {
      console.warn('⚠️ API request without auth token to:', config.url);
    }
    return config;
  },
  (error) => {
    console.error('API request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Refresh token on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('Received 401, attempting token refresh...');

      try {
        const refreshed = await keycloakAuth.refreshToken();
        if (refreshed) {
          console.log('Token refreshed successfully');
          const newToken = keycloakAuth.getToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient.request(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed, logging out:', refreshError);
        keycloakAuth.logout();
        return Promise.reject(refreshError);
      }
    }

    console.error('API error:', {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
    });

    return Promise.reject(error);
  }
);

export const voucherService = {
  createVoucher: async (data: VoucherCreateRequest): Promise<Voucher> => {
    const response = await apiClient.post('', data);
    return response.data;
  },

  getVoucher: async (voucherNumber: string): Promise<Voucher> => {
    const response = await apiClient.get(`/vouchers/${voucherNumber}`);
    return response.data;
  },

  processPayment: async (
    voucherNumber: string,
    data: VoucherPaymentRequest,
    idempotencyKey: string
  ): Promise<Voucher> => {
    const response = await apiClient.post(`/${voucherNumber}/payments`, data, {
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
    });
    return response.data;
  },

  searchVouchers: async (params?: {
    search?: string;
    page?: number;
    size?: number;
    filter?: string;
  }): Promise<{
    content: Voucher[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
  }> => {
    const response = await apiClient.get('/vouchers', { params });
    return response.data;
  },

  getVoucherStatusHistory: async (voucherNumber: string): Promise<StatusHistoryResponse[]> => {
    const response = await apiClient.get(`/vouchers/${voucherNumber}/status-history`);
    return response.data;
  },
  getVoucherTransactions: async (voucherNumber: string) => {
    const response = await apiClient.get(`/vouchers/${voucherNumber}/transactions`);
    return response.data;
  },
};

export const generateIdempotencyKey = (): string => {
  return crypto.randomUUID();
};

// Dashboard Service
export const dashboardService = {
  getMetrics: async () => {
    const response = await apiClient.get('/dashboard/metrics');
    return response.data;
  },
  getAlerts: async () => {
    const response = await apiClient.get('/dashboard/alerts');
    return response.data;
  },
  getRecentActivity: async (limit = 10) => {
    const response = await apiClient.get(`/dashboard/activity?limit=${limit}`);
    return response.data;
  },
  getDashboardMetrics: async () => {
    const response = await apiClient.get('/dashboard/metrics');
    return response.data;
  },
  getTransactionVolume: async () => {
    const response = await apiClient.get('/dashboard/transaction-volume');
    return response.data;
  },
  getVoucherStatusDistribution: async () => {
    const response = await apiClient.get('/dashboard/voucher-status');
    return response.data;
  },
  getSettlementTrends: async () => {
    const response = await apiClient.get('/dashboard/settlement-trends');
    return response.data;
  },
};


// Partner Service
export const partnerService = {
  // Get all partners with filtering
  getPartners: async (params?: any) => {
    const response = await apiClient.get('/partners', { params });
    return response.data;
  },
  // Get partner by ID
  getPartner: async (id: string) => {
    const response = await apiClient.get(`/partners/${id}`);
    return response.data;
  },
  // Get partner by code
  getPartnerByCode: async (partnerCode: string) => {
    const response = await apiClient.get(`/partners/code/${partnerCode}`);
    return response.data;
  },
  // Create merchant partner
  createMerchant: async (data: any) => {
    const response = await apiClient.post('/partners/merchant', data);
    return response.data;
  },
  // Create retailer partner
  createRetailer: async (data: any) => {
    const response = await apiClient.post('/partners/retailer', data);
    return response.data;
  },
  // Create agent partner
  createAgent: async (data: any) => {
    const response = await apiClient.post('/partners/agent', data);
    return response.data;
  },
  // Update partner
  updatePartner: async (id: string, data: any) => {
    const response = await apiClient.put(`/partners/${id}`, data);
    return response.data;
  },
  // Update partner status
  updatePartnerStatus: async (id: string, status: string, reason?: string) => {
    const params = new URLSearchParams({ status });
    if (reason) params.append('reason', reason);
    const response = await apiClient.put(`/partners/${id}/status?${params}`);
    return response.data;
  },
  // Delete partner
  deletePartner: async (id: string) => {
    const response = await apiClient.delete(`/partners/${id}`);
    return response.data;
  },
  // Get partner statistics
  getPartnerStatistics: async () => {
    const response = await apiClient.get('/partners/statistics');
    return response.data;
  },
  // Verify partner
  verifyPartner: async (id: string) => {
    const response = await apiClient.post(`/partners/${id}/verify`);
    return response.data;
  },
  // Suspend partner (ACTIVE to SUSPENDED)
  suspendPartner: async (id: string, reason: string) => {
    const params = new URLSearchParams({ reason });
    const response = await apiClient.post(`/partners/${id}/suspend?${params}`);
    return response.data;
  },
  // Activate partner (complete onboarding or reactivate from suspended)
  activatePartner: async (id: string) => {
    const response = await apiClient.post(`/partners/${id}/activate`);
    return response.data;
  },
  // Terminate partner (SUSPENDED to TERMINATED)
  terminatePartner: async (id: string) => {
    const params = new URLSearchParams({
      status: 'TERMINATED',
      reason: 'Partner terminated'
    });
    const response = await apiClient.put(`/partners/${id}/status?${params}`);
    return response.data;
  },
  // Partner status history
  getPartnerStatusHistory: async (partnerId: string) => {
    const response = await apiClient.get(`/partners/${partnerId}/status-history`);
    return response.data;
  },
  // Partner documents
  getPartnerDocuments: async (partnerId: string) => {
    const response = await apiClient.get(`/partners/${partnerId}/documents`);
    return response.data;
  },
  // Register partner
  registerPartner: async (data: any) => {
    const response = await apiClient.post('/partners/register', data);
    return response.data;
  },
  // Generate API key
  generateApiKey: async (partnerId: string) => {
    const response = await apiClient.post(`/partners/${partnerId}/api-keys`);
    return response.data;
  },
  // Get all API keys for a partner
  getApiKeys: async (partnerId: string) => {
    const response = await apiClient.get(`/partners/${partnerId}/api-keys`);
    return response.data;
  },
  // Send API key via email
  sendApiKeyEmail: async (partnerId: string, keyId: string) => {
    const response = await apiClient.post(`/partners/${partnerId}/api-keys/${keyId}/send-email`);
    return response.data;
  },
  // Revoke API key
  revokeApiKey: async (partnerId: string, keyId: string) => {
    const response = await apiClient.delete(`/partners/${partnerId}/api-keys/${keyId}`);
    return response.data;
  },
  // Approve partner (from PENDING_APPROVAL to PENDING_ONBOARDING)
  approvePartner: async (partnerId: string, comment?: string) => {
    const params = new URLSearchParams({
      status: 'PENDING_ONBOARDING',
      reason: comment || 'Application approved'
    });
    const response = await apiClient.put(`/partners/${partnerId}/status?${params}`);
    return response.data;
  },
  // Reject partner (from PENDING_APPROVAL back to DRAFT)
  rejectPartner: async (partnerId: string, reason: string) => {
    const params = new URLSearchParams({
      status: 'DRAFT',
      reason: reason || 'Application rejected'
    });
    const response = await apiClient.put(`/partners/${partnerId}/status?${params}`);
    return response.data;
  },
  // Submit partner for approval (from DRAFT to PENDING_APPROVAL)
  submitForApproval: async (partnerId: string) => {
    const params = new URLSearchParams({
      status: 'PENDING_APPROVAL',
      reason: 'Submitted for approval'
    });
    const response = await apiClient.put(`/partners/${partnerId}/status?${params}`);
    return response.data;
  },
  // Complete onboarding (from PENDING_ONBOARDING to ACTIVE)
  completeOnboarding: async (partnerId: string) => {
    const params = new URLSearchParams({
      status: 'ACTIVE',
      reason: 'Onboarding completed'
    });
    const response = await apiClient.put(`/partners/${partnerId}/status?${params}`);
    return response.data;
  },
  // Get API usage
  getApiUsage: async (partnerId: string) => {
    const response = await apiClient.get(`/partners/${partnerId}/api-usage`);
    return response.data;
  },
  // Get partner bank accounts
  getBankAccounts: async (partnerId: string) => {
    const response = await apiClient.get(`/partners/${partnerId}/banking-details`);
    return response.data;
  },
  // Update banking details
  updateBankingDetails: async (partnerId: string, data: {
    bankName: string;
    accountNumber: string;
    accountName: string;
    currency: string;
    branchCode?: string;
    swiftCode?: string;
    isPrimary?: boolean;
  }) => {
    const response = await apiClient.put(`/partners/${partnerId}/banking-details`, data);
    return response.data;
  },
  // Request limit increase
  requestLimitIncrease: async (partnerId: string, data: {
    limitType: 'DAILY_TRANSACTION' | 'MONTHLY_TRANSACTION' | 'SINGLE_TRANSACTION';
    requestedLimit: number;
    currentLimit: number;
    justification: string;
    businessReason: string;
  }) => {
    const response = await apiClient.post(`/partners/${partnerId}/request-limit-increase`, data);
    return response.data;
  },
  // Get settlement schedule
  getSettlementSchedule: async (partnerId: string) => {
    const response = await apiClient.get(`/partners/${partnerId}/settlement-schedule`);
    return response.data;
  },
  // Get partner summary statistics
  getPartnerSummary: async (partnerId: string) => {
    const response = await apiClient.get(`/partners/${partnerId}/summary`);
    return response.data;
  },
  // Legacy methods for compatibility
  getMerchants: async (params?: any) => {
    const response = await partnerService.getPartners({ ...params, partnerType: 'MERCHANT' });
    return response;
  },
  getMerchant: async (id: string) => {
    const response = await partnerService.getPartner(id);
    return response;
  },
  updateMerchant: async (id: string, data: any) => {
    const response = await partnerService.updatePartner(id, data);
    return response;
  },
  getRetailers: async (params?: any) => {
    const response = await partnerService.getPartners({ ...params, partnerType: 'RETAILER' });
    return response;
  },
  getRetailer: async (id: string) => {
    const response = await partnerService.getPartner(id);
    return response;
  },
  updateRetailer: async (id: string, data: any) => {
    const response = await partnerService.updatePartner(id, data);
    return response;
  },
};

// Transaction Service
export const transactionService = {
  getTransactions: async (params?: any) => {
    const response = await apiClient.get('/transactions', { params });
    return response.data;
  },
  getTransaction: async (id: string) => {
    const response = await apiClient.get(`/transactions/${id}`);
    return response.data;
  },
  searchTransactions: async (searchParams: any) => {
    const response = await apiClient.post('/transactions/search', searchParams);
    return response.data;
  },
};

// Settlement Service
export const settlementService = {
  // Admin: all settlements; partner: their own. Returns a Spring Page (read .content).
  getSettlements: async (params?: any) => {
    const response = await apiClient.get('/settlements', { params });
    return response.data;
  },
  getSettlement: async (batchNumber: string) => {
    const response = await apiClient.get(`/settlements/${batchNumber}`);
    return response.data;
  },
  // Create the daily settlement batches for all partners (DRAFT, awaiting approval).
  createSettlementBatch: async () => {
    const response = await apiClient.post('/settlements/daily-batch');
    return response.data;
  },
  approveSettlement: async (batchNumber: string, comments?: string) => {
    const response = await apiClient.post(`/settlements/${batchNumber}/approve`, {
      action: 'APPROVE',
      comments,
    });
    return response.data;
  },
  rejectSettlement: async (batchNumber: string, reason: string) => {
    const response = await apiClient.post(`/settlements/${batchNumber}/approve`, {
      action: 'REJECT',
      comments: reason,
    });
    return response.data;
  },
  // Process an approved settlement (posts the journal, runs delivery).
  processSettlement: async (batchNumber: string) => {
    const response = await apiClient.post(`/settlements/${batchNumber}/process`);
    return response.data;
  },
  downloadSettlementFile: async (batchNumber: string, format: 'csv' | 'pain001') => {
    const response = await apiClient.get(`/settlements/${batchNumber}/download?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Tariff Service (per-merchant fee tariffs + charge-bearer)
export const tariffService = {
  // List tariffs across all merchants (and global tariffs), paginated. Optionally scope to a
  // single merchant via `partner` (code or id, or the literal 'GLOBAL' is treated as "all").
  getTariffs: async (params?: { partner?: string; page?: number; size?: number }) => {
    const response = await apiClient.get('/tariffs', { params });
    return response.data; // Spring Page: { content, totalElements, ... }
  },
  getMerchantTariffs: async (partnerIdOrCode: string) => {
    const response = await apiClient.get(`/tariffs/merchant/${partnerIdOrCode}`);
    return response.data;
  },
  getTariff: async (id: string) => {
    const response = await apiClient.get(`/tariffs/${id}`);
    return response.data;
  },
  createTariff: async (data: any) => {
    const response = await apiClient.post('/tariffs', data);
    return response.data;
  },
  updateTariff: async (id: string, data: any) => {
    const response = await apiClient.put(`/tariffs/${id}`, data);
    return response.data;
  },
  deactivateTariff: async (id: string) => {
    const response = await apiClient.delete(`/tariffs/${id}`);
    return response.data;
  },
  previewCharge: async (merchant: string, amount: number, currency: string) => {
    const response = await apiClient.get('/tariffs/preview', {
      params: { merchant, amount, currency },
    });
    return response.data;
  },
};

// Audit Service
export const auditService = {
  getAuditLogs: async (params?: any) => {
    const response = await apiClient.get('/audit', { params });
    return response.data;
  },
  getAuditLog: async (id: string) => {
    const response = await apiClient.get(`/audit/${id}`);
    return response.data;
  },
  searchAuditLogs: async (searchParams: any) => {
    const response = await apiClient.post('/audit/search', searchParams);
    return response.data;
  },
};

// Report Service
export const reportService = {
  // Generate a new report
  generateReport: async (reportType: string, params: any) => {
    const response = await apiClient.post('/reports/generate', {
      reportType,
      ...params,
    });
    return response.data;
  },

  // Get report history with optional filtering
  getReportHistory: async (params?: any) => {
    const response = await apiClient.get('/reports/history', { params });
    return response.data;
  },

  // Get a specific report by ID
  getReport: async (reportId: string) => {
    const response = await apiClient.get(`/reports/${reportId}`);
    return response.data;
  },

  // Download report file
  downloadReport: async (reportId: string) => {
    const response = await apiClient.get(`/reports/${reportId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get report status
  getReportStatus: async (reportId: string) => {
    const response = await apiClient.get(`/reports/${reportId}/status`);
    return response.data;
  },

  // Delete a report
  deleteReport: async (reportId: string) => {
    const response = await apiClient.delete(`/reports/${reportId}`);
    return response.data;
  },

  // Schedule a recurring report
  scheduleReport: async (data: any) => {
    const response = await apiClient.post('/reports/schedule', data);
    return response.data;
  },

  // Get available report types
  getReportTypes: async () => {
    const response = await apiClient.get('/reports/types');
    return response.data;
  },
};

// Approval Service (Maker/Checker)
export const approvalService = {
  getPendingApprovals: async () => {
    const response = await apiClient.get('/approvals/pending');
    return response.data;
  },
  getApproval: async (id: string) => {
    const response = await apiClient.get(`/approvals/${id}`);
    return response.data;
  },
  getApprovalQueue: async (params?: any) => {
    const response = await apiClient.get('/approvals/queue', { params });
    return response.data;
  },
  approveRequest: async (id: string, comments?: string) => {
    const response = await apiClient.post(`/approvals/${id}/approve`, { comments });
    return response.data;
  },
  rejectRequest: async (id: string, reason: string) => {
    const response = await apiClient.post(`/approvals/${id}/reject`, { reason });
    return response.data;
  },
  returnRequest: async (id: string, data: { comment: string }) => {
    const response = await apiClient.post(`/approvals/${id}/return`, data);
    return response.data;
  },
  bulkApprove: async (ids: string[], comments?: string) => {
    const response = await apiClient.post('/approvals/bulk-approve', { ids, comments });
    return response.data;
  },
};

// System Configuration Service
export const configService = {
  getConfigurations: async () => {
    const response = await apiClient.get('/config');
    return response.data;
  },
  updateConfiguration: async (key: string, value: any) => {
    const response = await apiClient.put(`/config/${key}`, { value });
    return response.data;
  },
  getCommissionRates: async () => {
    const response = await apiClient.get('/config/commission-rates');
    return response.data;
  },
  updateCommissionRate: async (partnerId: string, rate: number) => {
    const response = await apiClient.put(`/config/commission-rates/${partnerId}`, { rate });
    return response.data;
  },
};

// Dispute Service
export const disputeService = {
  getDisputes: async (params?: any) => {
    const response = await apiClient.get('/disputes', { params });
    return response.data;
  },
  getDispute: async (id: string) => {
    const response = await apiClient.get(`/disputes/${id}`);
    return response.data;
  },
  createDispute: async (data: any) => {
    const response = await apiClient.post('/disputes', data);
    return response.data;
  },
  updateDispute: async (id: string, data: any) => {
    const response = await apiClient.put(`/disputes/${id}`, data);
    return response.data;
  },
  resolveDispute: async (id: string, resolution: any) => {
    const response = await apiClient.post(`/disputes/${id}/resolve`, resolution);
    return response.data;
  },
};

// Account Service
export const accountService = {
  // Initialize control accounts
  initializeControlAccounts: async () => {
    const response = await apiClient.post('/accounts/control-accounts/initialize');
    return response.data;
  },

  // Get partner accounts
  getPartnerAccounts: async (partnerId: string): Promise<Account[]> => {
    const response = await apiClient.get(`/accounts/partner/${partnerId}`);
    return response.data;
  },

  // Get control accounts
  getControlAccounts: async (): Promise<Account[]> => {
    const response = await apiClient.get('/accounts/control-accounts');
    return response.data;
  },

  // Get account by account number
  getAccount: async (accountNumber: string): Promise<Account> => {
    const response = await apiClient.get(`/accounts/${accountNumber}`);
    return response.data;
  },

  // Search accounts
  searchAccounts: async (params: AccountSearchParams): Promise<PaginatedResponse<Account>> => {
    const response = await apiClient.get('/accounts', { params });
    return response.data;
  },

  // Get account statement
  getAccountStatement: async (params: AccountStatementParams): Promise<AccountStatement> => {
    const { accountNumber, ...queryParams } = params;
    const response = await apiClient.get(`/accounts/${accountNumber}/statement`, { params: queryParams });
    return response.data;
  },

  // Load float
  loadFloat: async (request: FloatLoadRequest): Promise<FloatLoad> => {
    const response = await apiClient.post('/accounts/float/load', request);
    return response.data;
  },

  // Get float loads
  getFloatLoads: async (params?: any): Promise<PaginatedResponse<FloatLoad>> => {
    const response = await apiClient.get('/accounts/float/loads', { params });
    return response.data;
  },

  // Get float load by ID
  getFloatLoad: async (id: string): Promise<FloatLoad> => {
    const response = await apiClient.get(`/accounts/float/loads/${id}`);
    return response.data;
  },

  // Get transactions
  getTransactions: async (params: TransactionSearchParams): Promise<PaginatedResponse<AccountTransaction>> => {
    const response = await apiClient.get('/accounts/transactions', { params });
    return response.data;
  },

  // Get transaction by ID
  getTransaction: async (id: string): Promise<AccountTransaction> => {
    const response = await apiClient.get(`/accounts/transactions/${id}`);
    return response.data;
  },

  // Get transaction by reference
  getTransactionByReference: async (reference: string): Promise<AccountTransaction> => {
    const response = await apiClient.get(`/accounts/transactions/ref/${reference}`);
    return response.data;
  },

  // Get postings
  getPostings: async (params: PostingSearchParams): Promise<PaginatedResponse<AccountPosting>> => {
    const response = await apiClient.get('/accounts/postings', { params });
    return response.data;
  },

  // Get postings by transaction ID
  getTransactionPostings: async (transactionId: string): Promise<AccountPosting[]> => {
    const response = await apiClient.get(`/accounts/transactions/${transactionId}/postings`);
    return response.data;
  },

  // Get postings by account number
  getAccountPostings: async (accountNumber: string, params?: any): Promise<PaginatedResponse<AccountPosting>> => {
    const response = await apiClient.get(`/accounts/${accountNumber}/postings`, { params });
    return response.data;
  },
};

// Helper function to map backend user response to frontend User type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapUserResponse = (data: any): User => ({
  id: data.id?.toString() || '',
  username: data.username,
  email: data.email,
  firstName: data.firstName,
  lastName: data.lastName,
  phoneNumber: data.phoneNumber,
  roles: data.roles || [],
  status: data.userStatus || data.status || 'PENDING',
  partnerId: data.partnerId?.toString(),
  partnerName: data.partnerName,
  partnerType: data.partnerType,
  createdAt: data.dateCreated || data.createdAt || '',
  updatedAt: data.lastUpdated || data.updatedAt,
  lastLogin: data.lastLoginAt || data.lastLogin,
  emailVerified: data.emailVerified ?? false,
  twoFactorEnabled: data.twoFactorEnabled,
  suspendedAt: data.suspendedAt,
  suspensionReason: data.suspensionReason,
  createdBy: data.createdBy,
  updatedBy: data.updatedBy,
});

// User Management Service
export const userService = {
  // Get all users with filtering
  getUsers: async (params?: UserSearchParams): Promise<PaginatedUsersResponse> => {
    const response = await apiClient.get('/users', { params });
    return {
      ...response.data,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      content: (response.data.content || []).map((u: any) => mapUserResponse(u)),
    };
  },

  // Get user by ID
  getUser: async (userId: string): Promise<User> => {
    const response = await apiClient.get(`/users/${userId}`);
    return mapUserResponse(response.data);
  },

  // Get current user profile
  getCurrentUser: async (): Promise<UserProfile> => {
    const response = await apiClient.get('/users/me');
    return response.data;
  },

  // Create a new user
  createUser: async (data: CreateUserRequest): Promise<User> => {
    const response = await apiClient.post('/users', data);
    return mapUserResponse(response.data);
  },

  // Update user
  updateUser: async (userId: string, data: UpdateUserRequest): Promise<User> => {
    const response = await apiClient.put(`/users/${userId}`, data);
    return mapUserResponse(response.data);
  },

  // Update current user profile
  updateProfile: async (data: Partial<UpdateUserRequest>): Promise<UserProfile> => {
    const response = await apiClient.put('/users/me', data);
    return response.data;
  },

  // Delete user
  deleteUser: async (userId: string): Promise<void> => {
    await apiClient.delete(`/users/${userId}`);
  },

  // Suspend user
  suspendUser: async (userId: string, reason: string): Promise<User> => {
    const response = await apiClient.post(`/users/${userId}/suspend`, { reason });
    return mapUserResponse(response.data);
  },

  // Reactivate user
  reactivateUser: async (userId: string): Promise<User> => {
    const response = await apiClient.post(`/users/${userId}/reactivate`);
    return mapUserResponse(response.data);
  },

  // Update user password (by admin)
  resetUserPassword: async (data: ResetPasswordRequest): Promise<void> => {
    await apiClient.post(`/users/${data.userId}/reset-password`, { sendEmail: data.sendEmail });
  },

  // Update own password
  updatePassword: async (data: UpdatePasswordRequest): Promise<void> => {
    await apiClient.put('/users/me/password', data);
  },

  // Update user roles
  updateUserRoles: async (userId: string, roles: string[]): Promise<User> => {
    const response = await apiClient.put(`/users/${userId}/roles`, { roles });
    return mapUserResponse(response.data);
  },

  // Get user activity log
  getUserActivity: async (userId: string, params?: any): Promise<any> => {
    const response = await apiClient.get(`/users/${userId}/activity`, { params });
    return response.data;
  },

  // Enable/disable 2FA
  toggle2FA: async (enabled: boolean): Promise<void> => {
    await apiClient.put('/users/me/2fa', { enabled });
  },

  // Verify user email
  verifyEmail: async (userId: string): Promise<void> => {
    await apiClient.post(`/users/${userId}/verify-email`);
  },

  // Resend verification email
  resendVerificationEmail: async (userId: string): Promise<void> => {
    await apiClient.post(`/users/${userId}/resend-verification`);
  },

  // Get user statistics
  getUserStatistics: async (): Promise<UserStatistics> => {
    const response = await apiClient.get('/users/statistics');
    return response.data;
  },

  // Sync all users from Keycloak
  syncFromKeycloak: async () => {
    const response = await apiClient.post('/users/sync');
    return response.data;
  },

  // Request account setup email (public - no auth needed)
  requestSetupEmail: async (email: string): Promise<void> => {
    await apiClient.post('/account-setup/request-setup-email', { email });
  },
};

// Per-partner settlement-file delivery channel configuration (admin)
export interface DeliveryPreference {
  partnerCode: string;
  partnerName?: string;
  folderEnabled: boolean;
  folderPath?: string;
  queueEnabled: boolean;
  queueName?: string;
  emailEnabled: boolean;
  emailRecipient?: string;
  restEnabled: boolean;
}

export const deliveryPreferenceService = {
  getPreferences: async (partnerCode: string): Promise<DeliveryPreference> => {
    const response = await apiClient.get(`/delivery-preferences/${partnerCode}`);
    return response.data;
  },

  updatePreferences: async (
    partnerCode: string,
    data: Partial<Omit<DeliveryPreference, 'partnerCode' | 'partnerName'>>
  ): Promise<DeliveryPreference> => {
    const response = await apiClient.put(`/delivery-preferences/${partnerCode}`, data);
    return response.data;
  },
};